import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, getApiErrorCode, getApiErrorMessage, isEmailVerificationRequiredError } from '@/services/api';
import { featureFlags } from '@/config/featureFlags';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { 
  FileText, Upload, ArrowLeft, Loader2, Check, X, 
  AlertTriangle, Target, Lightbulb, Info, Sparkles
} from 'lucide-react';
import MainMenu from '@/components/MainMenu';
import SavedResumeSelector from '@/components/SavedResumeSelector';
import {
  getUnsupportedUploadFormatMessage,
  isSupportedUploadFile,
  LEGACY_DOC_GUIDANCE,
  SUPPORTED_UPLOAD_FORMAT_LABEL,
  UPLOAD_ACCEPT,
} from '@/lib/uploadFormats';

// AI Disclosure Component
function AIDisclosure() {
  return (
    <div className="flex items-start gap-2 p-3 bg-accent/10 border border-accent/20 rounded-lg text-sm">
      <Sparkles className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
      <p className="text-muted-foreground">
        <span className="font-medium text-foreground">AI-generated suggestions.</span>{' '}
        Always review and verify before submitting a resume.
      </p>
    </div>
  );
}

export default function ComparePage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const savedResumesEnabled = featureFlags.savedResumes;
  
  const [resumeText, setResumeText] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [savedResumesAvailable, setSavedResumesAvailable] = useState(savedResumesEnabled);
  const [savedResumes, setSavedResumes] = useState([]);
  const [savedResumesLoading, setSavedResumesLoading] = useState(false);
  const [loadingSavedResume, setLoadingSavedResume] = useState(false);
  const [selectedSavedResumeId, setSelectedSavedResumeId] = useState('');
  const [replacementNotice, setReplacementNotice] = useState('');
  const [pendingUploadedResumeSave, setPendingUploadedResumeSave] = useState(null);

  const savedResumeUiEnabled = savedResumesEnabled && savedResumesAvailable;

  const buildSavedResumeNameFromFilename = (filename) => {
    const cleanedFilename = (filename || '').trim();
    if (!cleanedFilename) {
      return undefined;
    }

    const filenameWithoutExtension = cleanedFilename.replace(/\.[^.]+$/, '').trim();
    return filenameWithoutExtension || cleanedFilename;
  };

  const hideSavedResumeUi = useCallback(() => {
    setSavedResumesAvailable(false);
    setSavedResumes([]);
    setSelectedSavedResumeId('');
    setPendingUploadedResumeSave(null);
  }, []);

  const refreshSavedResumes = useCallback(async ({ showErrorToast = false } = {}) => {
    if (!savedResumesEnabled || !savedResumesAvailable) {
      return;
    }

    setSavedResumesLoading(true);
    try {
      const data = await api.listSavedResumes();
      setSavedResumes(Array.isArray(data?.saved_resumes) ? data.saved_resumes : []);
    } catch (error) {
      if (getApiErrorCode(error) === 'saved_resumes_disabled') {
        hideSavedResumeUi();
        return;
      }

      if (showErrorToast) {
        toast.error(getApiErrorMessage(error, 'Failed to load saved resumes'));
      }
    } finally {
      setSavedResumesLoading(false);
    }
  }, [hideSavedResumeUi, savedResumesAvailable, savedResumesEnabled]);

  useEffect(() => {
    if (!savedResumesEnabled) {
      return;
    }

    refreshSavedResumes({ showErrorToast: true });
  }, [refreshSavedResumes, savedResumesEnabled]);

  const attemptSavePendingUploadedResume = async (currentResumeText) => {
    if (!savedResumeUiEnabled || !pendingUploadedResumeSave) {
      return currentResumeText;
    }

    const pendingResumeText = (pendingUploadedResumeSave.resumeText || '').trim();
    const currentTrimmedResumeText = (currentResumeText || '').trim();
    setPendingUploadedResumeSave(null);

    if (!pendingResumeText || pendingResumeText !== currentTrimmedResumeText) {
      return currentResumeText;
    }

    const payload = {
      resume_text: pendingResumeText,
      source: 'uploaded',
    };
    const savedResumeName = buildSavedResumeNameFromFilename(pendingUploadedResumeSave.filename);
    if (savedResumeName) {
      payload.name = savedResumeName;
    }

    try {
      const savedResume = await api.createSavedResume(payload);

      if (savedResume?.duplicate) {
        const existingResumeId = savedResume?.existing_resume?.id;
        if (existingResumeId) {
          try {
            const existingResume = await api.getSavedResume(existingResumeId);
            if (existingResume?.resume_text) {
              setResumeText(existingResume.resume_text);
              setSelectedSavedResumeId(existingResumeId);
              toast.info('This resume is already saved. We loaded the existing saved version instead.');
              await refreshSavedResumes();
              return existingResume.resume_text;
            }
          } catch (error) {
            if (getApiErrorCode(error) === 'saved_resumes_disabled') {
              hideSavedResumeUi();
              return currentResumeText;
            }

            toast.error(getApiErrorMessage(error, 'This resume is already saved, but the saved version could not be loaded right now.'));
            await refreshSavedResumes();
            return currentResumeText;
          }
        }

        toast.info('This resume is already saved. We loaded the existing saved version instead.');
        await refreshSavedResumes();
        return currentResumeText;
      }

      if (savedResume?.id) {
        setSelectedSavedResumeId(savedResume.id);
      }
      await refreshSavedResumes();
      return currentResumeText;
    } catch (error) {
      const errorCode = getApiErrorCode(error);

      if (errorCode === 'saved_resumes_disabled') {
        hideSavedResumeUi();
        return currentResumeText;
      }

      if (errorCode === 'saved_resume_limit_reached') {
        toast.info('Saved resume limit reached. You can keep using this uploaded resume, but it was not saved. Delete an old saved resume to save a new one.');
        return currentResumeText;
      }

      toast.error(getApiErrorMessage(error, 'Uploaded resume could not be saved for later.'));
      return currentResumeText;
    }
  };

  const handleSavedResumeSelect = async (savedResumeId) => {
    if (!savedResumeId) {
      return;
    }

    const hadExistingText = Boolean(resumeText.trim());

    setLoadingSavedResume(true);
    try {
      const savedResume = await api.getSavedResume(savedResumeId);
      setResumeText(savedResume.resume_text || '');
      setSelectedSavedResumeId(savedResumeId);
      setPendingUploadedResumeSave(null);
      setReplacementNotice(hadExistingText ? 'Previous text was replaced with the saved resume.' : '');
    } catch (error) {
      if (getApiErrorCode(error) === 'saved_resumes_disabled') {
        hideSavedResumeUi();
        return;
      }

      setSelectedSavedResumeId('');
      toast.error(getApiErrorMessage(error, 'Failed to load saved resume'));
    } finally {
      setLoadingSavedResume(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!isSupportedUploadFile(file)) {
      toast.error(getUnsupportedUploadFormatMessage());
      return;
    }

    const hadExistingText = Boolean(resumeText.trim());
    setUploadingFile(true);
    try {
      const data = await api.uploadResume(file);
      setResumeText(data.text);
      setReplacementNotice(savedResumesEnabled && hadExistingText ? 'Previous text was replaced with the uploaded resume.' : '');
      setSelectedSavedResumeId('');
      setPendingUploadedResumeSave(
        savedResumesEnabled && data.text?.trim()
          ? {
              resumeText: data.text,
              filename: data.filename || file.name,
            }
          : null
      );
      toast.success(`Extracted text from ${data.filename}`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to upload file'));
    } finally {
      setUploadingFile(false);
    }
  };

  const handleCompare = async () => {
    if (!resumeText.trim()) {
      toast.error('Please enter or upload your resume');
      return;
    }
    if (!jobDescription.trim()) {
      toast.error('Please enter the job description');
      return;
    }

    setLoading(true);
    try {
      const resumeTextToCompare = await attemptSavePendingUploadedResume(resumeText);
      const data = await api.compareResume(resumeTextToCompare, jobDescription);
      setResult(data);
      toast.success('Comparison complete!');
    } catch (error) {
      if (isEmailVerificationRequiredError(error)) {
        toast.error(error.response?.data?.detail || error.message || 'Please verify your email before using this feature.');
        navigate('/dashboard');
      } else if (error.response?.status === 403) {
        toast.error(error.response.data.detail);
        navigate('/pricing');
      } else {
        toast.error(error.response?.data?.detail || 'Comparison failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-success';
    if (score >= 60) return 'text-warning';
    return 'text-destructive';
  };

  const getScoreBg = (score) => {
    if (score >= 80) return 'bg-success';
    if (score >= 60) return 'bg-warning';
    return 'bg-destructive';
  };

  return (
    <div className="min-h-screen bg-surface">
      {/* Header */}
      <header className="bg-white border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
          <MainMenu />
          <button
            onClick={() => navigate('/dashboard')}
            className="text-muted-foreground hover:text-foreground transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-accent rounded-lg flex items-center justify-center">
              <Target className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading text-xl font-bold">Resume vs Job Comparison</span>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {!result ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Data Usage Notice */}
            <div className="flex items-start gap-2 p-3 bg-surface border border-border rounded-lg text-sm">
              <Info className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
              <p className="text-muted-foreground">
                Resumes are processed using AI to generate feedback. Avoid including sensitive personal data.
              </p>
            </div>

            {/* Split View */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Resume */}
              <div className="bg-white border border-border rounded-xl p-6 shadow-soft">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-heading text-lg font-semibold">Your Resume</h2>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingFile}
                    className="flex items-center gap-2 text-primary hover:text-primary-hover transition-colors text-sm"
                    data-testid="upload-btn"
                  >
                    {uploadingFile ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4" />
                    )}
                    Upload File
                  </button>
                </div>
                {savedResumeUiEnabled && (
                  <SavedResumeSelector
                    savedResumes={savedResumes}
                    selectedSavedResumeId={selectedSavedResumeId}
                    onSelectSavedResume={handleSavedResumeSelect}
                    loading={savedResumesLoading}
                    disabled={uploadingFile || loadingSavedResume}
                  />
                )}
                <p className="text-xs text-muted-foreground mb-3">
                  Supported: {SUPPORTED_UPLOAD_FORMAT_LABEL}. {LEGACY_DOC_GUIDANCE}
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={UPLOAD_ACCEPT}
                  onChange={handleFileUpload}
                  className="hidden"
                  data-testid="file-input"
                />
                <textarea
                  value={resumeText}
                  onChange={(e) => {
                    setResumeText(e.target.value);
                    setReplacementNotice('');
                    setPendingUploadedResumeSave(null);
                    setSelectedSavedResumeId('');
                  }}
                  placeholder="Paste your resume text here..."
                  className="w-full h-80 p-4 border border-border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-none font-mono text-sm"
                  data-testid="resume-textarea"
                />
                {savedResumesEnabled && replacementNotice && (
                  <p className="mt-2 text-xs text-muted-foreground">
                    {replacementNotice}
                  </p>
                )}
              </div>

              {/* Job Description */}
              <div className="bg-white border border-border rounded-xl p-6 shadow-soft">
                <div className="mb-4">
                  <h2 className="font-heading text-lg font-semibold">Job Description</h2>
                  <p className="text-sm text-muted-foreground">Paste the job posting</p>
                </div>
                <textarea
                  value={jobDescription}
                  onChange={(e) => setJobDescription(e.target.value)}
                  placeholder="Paste the job description here..."
                  className="w-full h-80 p-4 border border-border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-none font-mono text-sm"
                  data-testid="job-textarea"
                />
              </div>
            </div>

            {/* Compare Button */}
            <button
              onClick={handleCompare}
              disabled={loading || !resumeText.trim() || !jobDescription.trim()}
              className="w-full bg-accent text-white py-4 rounded-xl font-medium hover:bg-accent/90 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="compare-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Comparing...
                </>
              ) : (
                <>
                  <Target className="w-5 h-5" />
                  Compare Resume to Job
                </>
              )}
            </button>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* AI Disclosure */}
            <AIDisclosure />

            {/* Score Cards */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white border border-border rounded-xl p-8 shadow-soft text-center">
                <h2 className="font-heading text-lg font-semibold mb-4">Match Score</h2>
                <div className="relative w-32 h-32 mx-auto mb-4">
                  <svg className="w-full h-full transform -rotate-90">
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      stroke="currentColor"
                      strokeWidth="8"
                      fill="none"
                      className="text-surface"
                    />
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      stroke="currentColor"
                      strokeWidth="8"
                      fill="none"
                      className={getScoreBg(result.overall_score)}
                      strokeDasharray={`${(result.overall_score / 100) * 352} 352`}
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className={`font-heading text-4xl font-bold ${getScoreColor(result.overall_score)}`}>
                      {result.overall_score}%
                    </span>
                  </div>
                </div>
                <p className="text-muted-foreground text-sm">How well you match this job</p>
              </div>

              <div className="bg-white border border-border rounded-xl p-8 shadow-soft text-center">
                <h2 className="font-heading text-lg font-semibold mb-4">ATS Compatibility</h2>
                <div className="relative w-32 h-32 mx-auto mb-4">
                  <svg className="w-full h-full transform -rotate-90">
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      stroke="currentColor"
                      strokeWidth="8"
                      fill="none"
                      className="text-surface"
                    />
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      stroke="currentColor"
                      strokeWidth="8"
                      fill="none"
                      className={getScoreBg(result.ats_compatibility_score)}
                      strokeDasharray={`${(result.ats_compatibility_score / 100) * 352} 352`}
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className={`font-heading text-4xl font-bold ${getScoreColor(result.ats_compatibility_score)}`}>
                      {result.ats_compatibility_score}%
                    </span>
                  </div>
                </div>
                <p className="text-muted-foreground text-sm">ATS system compatibility</p>
              </div>
            </div>

            {/* Results Grid */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Keyword Matches */}
              <div className="bg-white border border-border rounded-xl p-6 shadow-soft" data-testid="matches-card">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 bg-success/10 rounded-lg flex items-center justify-center">
                    <Check className="w-5 h-5 text-success" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold">Keywords Found</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {result.keyword_matches.map((keyword, i) => (
                    <span key={i} className="px-3 py-1 bg-success/10 text-success rounded-full text-sm">
                      {keyword}
                    </span>
                  ))}
                </div>
              </div>

              {/* Missing Keywords */}
              <div className="bg-white border border-border rounded-xl p-6 shadow-soft" data-testid="missing-keywords-card">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 bg-warning/10 rounded-lg flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-warning" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold">Missing Keywords</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {result.missing_keywords.map((keyword, i) => (
                    <span key={i} className="px-3 py-1 bg-warning/10 text-warning rounded-full text-sm">
                      {keyword}
                    </span>
                  ))}
                </div>
              </div>

              {/* Missing Skills */}
              <div className="bg-white border border-border rounded-xl p-6 shadow-soft" data-testid="missing-skills-card">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 bg-destructive/10 rounded-lg flex items-center justify-center">
                    <X className="w-5 h-5 text-destructive" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold">Missing Skills</h3>
                </div>
                <ul className="space-y-2">
                  {result.missing_skills.map((skill, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <X className="w-4 h-4 text-destructive flex-shrink-0 mt-0.5" />
                      <span>{skill}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Suggestions */}
              <div className="bg-white border border-border rounded-xl p-6 shadow-soft" data-testid="suggestions-card">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Lightbulb className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold">Suggestions</h3>
                </div>
                <ul className="space-y-2">
                  {result.suggestions.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <Target className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => setResult(null)}
                className="flex-1 bg-white border border-border py-3 rounded-xl font-medium hover:bg-surface transition-all"
                data-testid="compare-another-btn"
              >
                Compare Another
              </button>
              <button
                onClick={() => navigate('/builder')}
                className="flex-1 bg-primary text-white py-3 rounded-xl font-medium hover:bg-primary-hover transition-all"
                data-testid="build-resume-btn"
              >
                Build Optimized Resume
              </button>
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
}
